import { Injectable } from "@angular/core";
import { Observable, of, BehaviorSubject } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { Helper } from "src/app/core/helpers/helper";
import { environment } from "src/environments/environment.prod";

declare let google: any;

@Injectable({
  providedIn: "root"
})
export class GoogleMapsService {
  private autocomplete: any;
  private placesService: any;
  private sessionToken: any;
  private currentLocation: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private readonly themeLang = localStorage.getItem("theme_lang") ?? environment.defaultLang;

  constructor(private helper: Helper) {
    this.initializeLocation();
  }

  async createAutocompleteInstance(): Promise<void> {
    if (!this.autocomplete) {
      const { AutocompleteSuggestion } = await google.maps.importLibrary("places");
      this.autocomplete = AutocompleteSuggestion;
    }
  }

  createPlacesServiceInstance(): void {
    if (!this.placesService) {
      this.placesService = new google.maps.places.PlacesService(document.createElement("div"));
    }
  }

  private initializeLocation(): void {
    this.checkLocationPermission()
      .pipe(
        switchMap((isGranted) => {
          if (isGranted) {
            return this.getCurrentLocation();
          }
          return of(null);
        }),
        take(1)
      )
      .subscribe((location) => {
        this.currentLocation.next(location);
      });
  }

  async getPlacePredictions(input: string, params = {}): Promise<any> {
    try {
      if (!input) return [];
      // Ensure the autocomplete instance is created
      await this.createAutocompleteInstance();
      // Initialize session token if not already done
      if (!this.sessionToken) {
        const { AutocompleteSessionToken } = await google.maps.importLibrary("places");
        this.sessionToken = new AutocompleteSessionToken(); // Ensure to call the constructor
      }

      // Check if the autocomplete service is available

      if (!this.autocomplete) {
        throw new Error("Autocomplete service is not initialized");
      }

      // Set base params
      const Params: any = {
        input: input,
        sessionToken: this.sessionToken,
        language: this.themeLang,
        ...params
      };

      // Add location bias if user coordinates are available
      if (this.currentLocation) {
        const coords = await this.currentLocation.pipe(take(1)).toPromise();

        if (coords && !(coords instanceof Error)) {
          Params.locationBias = {
            center: { lat: coords.latitude, lng: coords.longitude }, // Set bias center
            radius: 20000 // 20 km radius
          };
        }
      }
      // Use async/await instead of .then() and .catch()
      const { suggestions } = await this.autocomplete.fetchAutocompleteSuggestions({
        input: input,
        sessionToken: this.sessionToken,
        language: this.themeLang,
        ...Params
      });

      for (const suggestion of suggestions) {
        const placePrediction = suggestion.placePrediction;
        const text = placePrediction!.text.toString();
        const placeId = placePrediction!.placeId;
        suggestion["description"] = text;
        suggestion["place_id"] = placeId;
      }
      return suggestions;
    } catch (error) {
      // Handle any errors in the try block
      console.error("Error in getPlacePredictions:", error);
      throw error; // Rethrow or handle the error as needed
    }
  }

  getPlaceDetails(placeId: string): Observable<any> {
    this.createPlacesServiceInstance();

    return new Observable((observer) => {
      const request = {
        placeId: placeId,
        fields: ["name", "geometry", "address_components", "formatted_address", "types"],
        sessionToken: this.sessionToken
      };

      this.placesService.getDetails(request, (place: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          observer.next(place);
          this.sessionToken = undefined; // Clear session token after use
        } else {
          observer.error(status);
        }
        observer.complete();
      });
    });
  }

  private checkLocationPermission(): Observable<boolean> {
    return new Observable((observer) => {
      navigator.permissions.query({ name: "geolocation" }).then((permissionStatus) => {
        observer.next(permissionStatus.state === "granted");
        observer.complete();
      });
    });
  }

  private getCurrentLocation(): Observable<any> {
    return new Observable((observer) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          observer.next(position.coords);
          observer.complete();
        },
        (error) => {
          console.error("Error getting user location:", error);
          observer.next(null);
          observer.complete();
        }
      );
    });
  }

  async getDistances(origin: any, destinations: any[]): Promise<any> {
    if (this.helper.mapTypeMapBox()) {
      // Mapbox setup
      const url = "https://api.mapbox.com/directions/v5/mapbox/driving"; // Mapbox Directions API URL
      const accessToken = this.helper.MapBoxAccessToken; // Mapbox Access Token
      
      // Convert the origin to Mapbox's required [lng, lat] format (ensure it's [longitude, latitude])
      const originCoord = [origin.latLng.longitude, origin.latLng.latitude];  // Ensure it's [lng, lat]
      
      // Convert destinations to Mapbox's required [lng, lat] format
      const coordinates = [originCoord, ...destinations.map((dest: any) => [
        dest.waypoint.location.latLng.longitude, 
        dest.waypoint.location.latLng.latitude
      ])];
      
      try {
        // Construct the Mapbox URL with all coordinates
        const urlWithCoords = `${url}/${coordinates.map((coord: any) => coord.join(',')).join(';')}?access_token=${accessToken}`;
        
        const response = await fetch(urlWithCoords, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching Mapbox directions: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Mapbox Directions API returns routes in the "routes" array
        const distances = data.routes.map((route: any) => {
          return {
            distance: route.distance,  // Distance in meters
            duration: route.duration,  // Duration in seconds
          };
        });

        return distances; // Return the distances and durations for each route

      } catch (error) {
        console.error("Error fetching Mapbox directions", error);
        throw error;
      }
    } else if (this.helper.mapTypeGoogle()) {
      const url = this.helper.mapDistanceMatrixUrl; // Update with actual API endpoint
      const key = this.helper.mapKey; // Make sure to replace with a valid API key
      const requestBody = {
        origins: [{ waypoint: { location: origin } }],
        destinations: destinations,
        travelMode: "DRIVE",
        routingPreference: "TRAFFIC_AWARE"
      };
  
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": key,
            "X-Goog-FieldMask": "originIndex,destinationIndex,duration,distanceMeters,status,condition"
          },
          body: JSON.stringify(requestBody)
        });
  
        if (!response.ok) {
          throw new Error(`Error fetching distance matrix data: ${response.statusText}`);
        }
  
        return await response.json();
      } catch (error) {
        console.error("Error fetching distance matrix data", error);
        throw error;
      }
    }

  }

  async fetchFields(prediction: any): Promise<any> {
    const place = prediction.placePrediction.toPlace();
    await place.fetchFields({
      fields: ["displayName", "formattedAddress", "location", "addressComponents", "types"]
    });
    place["geometry"] = {
      location: place.location // Ensuring geometry.location remains accessible
    };
    place["formatted_address"] = place.formattedAddress;
    place["address_components"] = place.addressComponents;
    return place;
  }
}
